import { Language, TextKeyWithOnlyText } from '../types/Antiloop.js'
import { CompactTextEntry, CompactTexts, TextStyle, StyleResolver } from '../types/CompactTextTypes.js'
import designSystem from '../assets/design-system.json'
import compactTexts from '../assets/compact-texts.json'

// Cache for resolved styles
const styleCache = new Map<string, TextStyle>()

/**
 * Get compact text entry by key
 */
export const getCompactTextEntry = (key: string): CompactTextEntry | null => {
  return (compactTexts as CompactTexts)[key] || null
}

/**
 * Get default text style when no style reference is provided
 */
export const getDefaultTextStyle = (): TextStyle => {
  return {
    fontFamily: 'Plus Jakarta Sans',
    fontSize: '16px',
    fontWeight: 400,
    letterSpacing: '0px',
    lineHeight: '24px',
    color: 'rgba(0, 11, 97, 1)'
  }
}

/**
 * Enhanced getFigmaText function with backward compatibility
 * Supports both new compact format and legacy TextKeyWithOnlyText format
 * Now includes support for customer-specific translation overrides
 *
 * Fallback chain:
 * 1. {language}-{customerId} - Customer-specific translation for requested language
 * 2. {language} - Standard translation for requested language
 * 3. sv-{customerId} - Customer-specific Swedish translation
 * 4. sv - Standard Swedish translation (ultimate fallback)
 */
export const getFigmaText = (
  textKey: TextKeyWithOnlyText | string,
  language: Language = 'sv',
  customerId?: string
): string => {
  // Use process.env for language override in Node.js
  const lang = process.env.VITE_LANGUAGE || language

  // Handle new compact format (string key)
  if (typeof textKey === 'string') {
    const compactEntry = getCompactTextEntry(textKey)
    if (compactEntry) {
      // 1. Try customer-specific translation for requested language
      if (customerId && compactEntry.c?.[`${lang}-${customerId}`]) {
        return compactEntry.c[`${lang}-${customerId}`]
      }

      // 2. Try standard language translation
      if (compactEntry.l?.[lang]) {
        return compactEntry.l[lang]
      }

      // 3. Try customer-specific Swedish translation
      if (customerId && compactEntry.c?.[`sv-${customerId}`]) {
        return compactEntry.c[`sv-${customerId}`]
      }

      // 4. Ultimate fallback to Swedish
      return compactEntry.l?.sv || ''
    }
  }

  // Handle legacy format (TextKeyWithOnlyText object)
  if (textKey && typeof textKey === 'object') {
    return textKey.texts?.[lang] || textKey.characters || ''
  }

  return ''
}

/**
 * Get text style from design system or legacy format
 */
export const getTextStyle = (textKey: TextKeyWithOnlyText | string): TextStyle => {
  // Handle new compact format with style reference
  if (typeof textKey === 'string') {
    const compactEntry = getCompactTextEntry(textKey)
    if (compactEntry?.s) {
      const resolvedStyle = getFlatDesignSystemStyle(compactEntry.s)
      if (resolvedStyle) {
        return resolvedStyle
      }
    }
  }

  // Handle legacy format
  if (textKey && typeof textKey === 'object' && textKey.style) {
    return textKey.style as TextStyle
  }

  // Return default style
  return getDefaultTextStyle()
}

/**
 * Get all compact text entries
 */
export const getAllCompactTexts = (): CompactTexts => {
  return compactTexts as CompactTexts
}

/**
 * Get compact text entries by category
 * Note: This function is deprecated since metadata was removed from compact-texts.json
 * Use centralized styling from design-system.json instead
 */
export const getCompactTextsByCategory = (category: string): CompactTextEntry[] => {
  console.warn('getCompactTextsByCategory is deprecated. Use centralized styling from design-system.json instead.')
  return []
}

/**
 * Get compact text entries by page
 * Note: This function is deprecated since metadata was removed from compact-texts.json
 * Use centralized styling from design-system.json instead
 */
export const getCompactTextsByPage = (page: string): CompactTextEntry[] => {
  console.warn('getCompactTextsByPage is deprecated. Use centralized styling from design-system.json instead.')
  return []
}

/**
 * Check if a text key exists in compact format
 */
export const hasCompactText = (key: string): boolean => {
  return key in (compactTexts as CompactTexts)
}

/**
 * Get a flat design system style by key
 * Supports both flat keys (e.g., "h1") and nested keys (e.g., "body.regular", "headings.h3", "button.primary")
 *
 * Resolution strategy:
 * 1. Try typography.{styleKey} (e.g., "h1" -> typography.h1)
 * 2. Try {topLevelKey}.{restOfPath} (e.g., "button.primary" -> button.primary)
 * 3. Try typography.{fullPath} (e.g., "body.regular" -> typography.body.regular)
 */
export const getFlatDesignSystemStyle = (styleKey: string): TextStyle | null => {
  if (styleCache.has(styleKey)) {
    return styleCache.get(styleKey)!
  }

  const ds = designSystem as any

  // Strategy 1: Try as a direct typography key (flat keys like "h1", "regular")
  if (
    ds.typography?.[styleKey] &&
    typeof ds.typography[styleKey] === 'object' &&
    'fontFamily' in ds.typography[styleKey]
  ) {
    styleCache.set(styleKey, ds.typography[styleKey])
    return ds.typography[styleKey]
  }

  // Strategy 2: If key contains dots, try as top-level category first
  // e.g., "button.primary" -> button.primary, "navigation.header" -> navigation.header
  if (styleKey.includes('.')) {
    const parts = styleKey.split('.')
    const topLevel = parts[0]

    // Try direct path from root (for button, navigation, etc.)
    let current: any = ds[topLevel]
    for (let i = 1; i < parts.length; i++) {
      if (current && typeof current === 'object' && parts[i] in current) {
        current = current[parts[i]]
      } else {
        current = null
        break
      }
    }

    if (current && typeof current === 'object' && 'fontFamily' in current) {
      styleCache.set(styleKey, current)
      return current
    }

    // Strategy 3: Try under typography (for nested typography like "body.regular", "headings.h3")
    current = ds.typography?.[topLevel]
    for (let i = 1; i < parts.length; i++) {
      if (current && typeof current === 'object' && parts[i] in current) {
        current = current[parts[i]]
      } else {
        current = null
        break
      }
    }

    if (current && typeof current === 'object' && 'fontFamily' in current) {
      styleCache.set(styleKey, current)
      return current
    }
  }

  if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
    console.warn(`Style not found: ${styleKey}`)
  }
  return null
}
